/**
 * API Response Validator
 *
 * Helper utilities for validating API responses with Zod schemas.
 * Provides typed, validated responses with error handling.
 */

import { type ZodSchema, ZodError } from 'zod';

/**
 * Validation result types
 */
export type ValidationSuccess<T> = {
  success: true;
  data: T;
};

export type ValidationError = {
  success: false;
  error: string;
  details?: unknown;
};

export type ValidationResult<T> = ValidationSuccess<T> | ValidationError;

/**
 * Validate API response data with a Zod schema
 *
 * @param data - Raw data from API response
 * @param schema - Zod schema to validate against
 * @param context - Optional context for error messages
 * @returns Validated data or throws ZodError
 *
 * @example
 * ```typescript
 * import { validateResponse } from '@/lib/api/apiValidator';
 * import { VendorDetailSchema } from '@/types/api/generated-schemas';
 *
 * const response = await fetch('/api/vendors/1');
 * const rawData = await response.json();
 * const vendor = validateResponse(rawData, VendorDetailSchema, 'GET /api/vendors/:id');
 * ```
 */
export function validateResponse<T>(
  data: unknown,
  schema: ZodSchema<T>,
  context?: string
): T {
  try {
    return schema.parse(data);
  } catch (error) {
    if (error instanceof ZodError) {
      const contextMsg = context ? ` (${context})` : '';
      console.error(`API Response validation failed${contextMsg}:`, error.errors);
      throw error;
    }
    throw error;
  }
}

/**
 * Safely validate API response data (non-throwing)
 *
 * @param data - Raw data from API response
 * @param schema - Zod schema to validate against
 * @param context - Optional context for error messages
 * @returns ValidationResult with success flag and data/error
 *
 * @example
 * ```typescript
 * import { safeValidateResponse } from '@/lib/api/apiValidator';
 * import { VendorDetailSchema } from '@/types/api/generated-schemas';
 *
 * const response = await fetch('/api/vendors/1');
 * const rawData = await response.json();
 * const result = safeValidateResponse(rawData, VendorDetailSchema);
 *
 * if (result.success) {
 *   console.log('Vendor:', result.data);
 * } else {
 *   console.error('Validation error:', result.error);
 * }
 * ```
 */
export function safeValidateResponse<T>(
  data: unknown,
  schema: ZodSchema<T>,
  context?: string
): ValidationResult<T> {
  const result = schema.safeParse(data);

  if (result.success) {
    return {
      success: true,
      data: result.data,
    };
  }

  const contextMsg = context ? ` (${context})` : '';
  console.error(`API Response validation failed${contextMsg}:`, result.error.errors);

  return {
    success: false,
    error: formatZodError(result.error),
    details: result.error.errors,
  };
}

/**
 * Format Zod error into a user-friendly message
 */
function formatZodError(error: ZodError): string {
  const firstError = error.errors[0];
  if (!firstError) return 'Validation failed';

  const path = firstError.path.join('.');
  const message = firstError.message;

  if (path) {
    return `Invalid ${path}: ${message}`;
  }

  return message;
}

/**
 * Validate and fetch API response in one call
 *
 * @param url - API endpoint URL
 * @param schema - Zod schema to validate response against
 * @param options - Fetch options
 * @returns Validated data
 *
 * @example
 * ```typescript
 * import { fetchAndValidate } from '@/lib/api/apiValidator';
 * import { VendorDetailSchema } from '@/types/api/generated-schemas';
 *
 * const vendor = await fetchAndValidate(
 *   '/api/vendors/1',
 *   VendorDetailSchema
 * );
 * ```
 */
export async function fetchAndValidate<T>(
  url: string,
  schema: ZodSchema<T>,
  options?: RequestInit
): Promise<T> {
  const response = await fetch(url, options);

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const rawData = await response.json();
  return validateResponse(rawData, schema, `${options?.method || 'GET'} ${url}`);
}

/**
 * Safely fetch and validate API response (non-throwing)
 *
 * @param url - API endpoint URL
 * @param schema - Zod schema to validate response against
 * @param options - Fetch options
 * @returns ValidationResult with success flag and data/error
 *
 * @example
 * ```typescript
 * import { safeFetchAndValidate } from '@/lib/api/apiValidator';
 * import { VendorDetailSchema } from '@/types/api/generated-schemas';
 *
 * const result = await safeFetchAndValidate(
 *   '/api/vendors/1',
 *   VendorDetailSchema
 * );
 *
 * if (result.success) {
 *   console.log('Vendor:', result.data);
 * } else {
 *   console.error('Error:', result.error);
 * }
 * ```
 */
export async function safeFetchAndValidate<T>(
  url: string,
  schema: ZodSchema<T>,
  options?: RequestInit
): Promise<ValidationResult<T>> {
  try {
    const response = await fetch(url, options);

    if (!response.ok) {
      return {
        success: false,
        error: `HTTP ${response.status}: ${response.statusText}`,
      };
    }

    const rawData = await response.json();
    return safeValidateResponse(rawData, schema, `${options?.method || 'GET'} ${url}`);
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      details: error,
    };
  }
}
